import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid2,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { formatDate } from "../../../../../../views/components/common/functions/formatDate";
import TableState from "../../../../../../views/components/common/table-states/TableState";
import {
  InventoryOpsPoDetail,
  InventoryOpsSizeMix,
} from "../../../../../../models/ProjectManagement/ProjectInventoryProduct";
import { getNetInwardQty } from "./inventoryOpsPoTaskStatus";
import {
  buildInventoryOpsVariantTrackingRows,
  InventoryOpsVariantTrackingRow,
} from "./inventoryOpsTrackingSummary";

interface InventoryOpsVariantTrackingSummaryProps {
  sizeMixes: InventoryOpsSizeMix[] | null | undefined;
  po: InventoryOpsPoDetail | null | undefined;
  showPrintColumns?: boolean;
}

interface JourneyStep {
  key: string;
  label: string;
  qty: number;
  firstAt: string | Date | null;
  lastAt: string | Date | null;
  completed: boolean;
}

const formatQty = (value: number) => (value > 0 ? String(value) : "—");

const formatWhen = (value: string | Date | null) =>
  value ? formatDate(value) : "—";

const JourneyStepCard: React.FC<{
  step: JourneyStep;
  isLast: boolean;
}> = ({ step, isLast }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "stretch",
        flex: "1 1 0",
        minWidth: 140,
      }}
    >
      <Box
        sx={{
          flex: 1,
          border: `1px solid ${
            step.completed
              ? theme.palette.customColors.EmeraldGreen
              : theme.palette.customColors.greyBorder
          }`,
          borderRadius: "8px",
          backgroundColor: step.completed
            ? theme.palette.customColors.lightGreenBackground
            : theme.palette.customColors.greyBackground,
          px: 1.25,
          py: 1,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.75,
          }}
        >
          <CustomTypography
            variant="caption"
            fontWeight={700}
            color={theme.palette.customColors.greyTextColor}
          >
            {step.label}
          </CustomTypography>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              flexShrink: 0,
            }}
            title={step.completed ? "Completed" : "Pending"}
          >
            <TableState state={step.completed} height="14px" width="14px" />
            <CustomTypography
              variant="caption"
              color={
                step.completed
                  ? theme.palette.customColors.EmeraldGreen
                  : theme.palette.customColors.greyTextColor
              }
            >
              {step.completed ? "Done" : "Pending"}
            </CustomTypography>
          </Box>
        </Box>
        <CustomTypography variant="heading7" fontWeight={700}>
          {formatQty(step.qty)}
        </CustomTypography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          <CustomTypography
            variant="caption"
            color={theme.palette.customColors.greyTextColor}
          >
            First: {formatWhen(step.firstAt)}
          </CustomTypography>
          <CustomTypography
            variant="caption"
            color={theme.palette.customColors.greyTextColor}
          >
            Last: {formatWhen(step.lastAt)}
          </CustomTypography>
        </Box>
      </Box>
      {!isLast ? (
        <Box
          sx={{
            width: 28,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: 2,
              backgroundColor: step.completed
                ? theme.palette.customColors.EmeraldGreen
                : theme.palette.customColors.greyBorder,
              borderRadius: 1,
            }}
          />
        </Box>
      ) : null}
    </Box>
  );
};

const buildJourneySteps = (
  row: InventoryOpsVariantTrackingRow,
  showPrintColumns: boolean,
): JourneyStep[] => {
  const netRawGood = getNetInwardQty(row.rawInwardQty, row.rawRejectedQty);
  const approvedForPrint = netRawGood;

  const steps: JourneyStep[] = [
    {
      key: "inward",
      label: "Inward",
      qty: row.rawInwardQty,
      firstAt: row.rawInwardFirstAt,
      lastAt: row.rawInwardLastAt,
      completed: (() => {
        const printRejected = showPrintColumns ? row.printRejectedQty : 0;
        const effective = Math.max(0, netRawGood - printRejected);
        return row.orderedQty > 0
          ? effective >= row.orderedQty
          : row.rawInwardQty > 0;
      })(),
    },
    {
      key: "rejected",
      label: "Rejected",
      qty: row.rawRejectedQty,
      firstAt: row.rawQcFirstAt,
      lastAt: row.rawQcLastAt,
      // QC stage is done once a QC record exists for an inwarded SKU.
      completed: row.rawInwardQty > 0 && row.rawQcLastAt != null,
    },
  ];

  if (showPrintColumns) {
    steps.push(
      {
        key: "print-out",
        label: "Print Out",
        qty: row.printOutwardQty,
        firstAt: row.printOutwardFirstAt,
        lastAt: row.printOutwardLastAt,
        completed:
          approvedForPrint > 0 && row.printOutwardQty >= approvedForPrint,
      },
      {
        key: "print-in",
        label: "Print In",
        qty: row.printInwardQty,
        firstAt: row.printInwardFirstAt,
        lastAt: row.printInwardLastAt,
        completed:
          row.printOutwardQty > 0 && row.printInwardQty >= row.printOutwardQty,
      },
      {
        key: "print-rejected",
        label: "Print Rejected",
        qty: row.printRejectedQty,
        firstAt: row.printQcFirstAt,
        lastAt: row.printQcLastAt,
        completed: row.printInwardQty > 0 && row.printQcLastAt != null,
      },
    );
  }

  return steps;
};

const VariantJourneyRow: React.FC<{
  row: InventoryOpsVariantTrackingRow;
  showPrintColumns: boolean;
}> = ({ row, showPrintColumns }) => {
  const theme = useTheme();
  const steps = buildJourneySteps(row, showPrintColumns);

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Grid2 container spacing={1.5} alignItems="flex-start">
        <Grid2
          display="flex"
          flexDirection="column"
          size={{ xs: 12, md: 3 }}
          gap={0.5}
        >
          <CustomTypography variant="heading8" fontWeight={700}>
            {row.title}
          </CustomTypography>
          <CustomTypography variant="body2">
            {row.sku ? `${row.sku}` : ""}
          </CustomTypography>
          <CustomTypography
            variant="caption"
            color={theme.palette.customColors.greyTextColor}
            style={{ marginTop: 4, display: "block" }}
          >
            Ordered: {formatQty(row.orderedQty)}
          </CustomTypography>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 9 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "stretch",
              gap: { xs: 1, md: 0 },
              overflowX: "auto",
              pb: 0.5,
            }}
          >
            {steps.map((step, index) => (
              <JourneyStepCard
                key={step.key}
                step={step}
                isLast={index === steps.length - 1}
              />
            ))}
          </Box>
        </Grid2>
      </Grid2>
    </Box>
  );
};

const InventoryOpsVariantTrackingSummary: React.FC<
  InventoryOpsVariantTrackingSummaryProps
> = ({ sizeMixes, po, showPrintColumns = false }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const rows: InventoryOpsVariantTrackingRow[] =
    buildInventoryOpsVariantTrackingRows(sizeMixes, po);

  if (rows.length === 0) return null;

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, next) => setExpanded(next)}
      disableGutters
      elevation={0}
      sx={{
        mb: 1.5,
        border: `1px solid ${theme.palette.customColors.greyBorder}`,
        borderRadius: "8px !important",
        overflow: "hidden",
        "&::before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 2,
          minHeight: 40,
          backgroundColor: theme.palette.customColors.greyBackground,
          "& .MuiAccordionSummary-content": { my: 0.5 },
        }}
      >
        <Box>
          <CustomTypography variant="body2" fontWeight={700}>
            Variant tracking summary
          </CustomTypography>
          <CustomTypography
            variant="caption"
            color={theme.palette.customColors.greyTextColor}
          >
            {rows.length} variant{rows.length === 1 ? "" : "s"} · journey of
            inward, QC
            {showPrintColumns ? ", print out & print in" : ""}
          </CustomTypography>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0 }}>
        <Box
          sx={{
            px: 2,
            py: 1,
            backgroundColor: theme.palette.customColors.greyBackground,
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <CustomTypography
            variant="caption"
            color={theme.palette.customColors.greyTextColor}
          >
            Each step shows qty / first / last · Done / Pending status
          </CustomTypography>
        </Box>
        <Divider />
        {rows.map((row) => (
          <VariantJourneyRow
            key={row.sku}
            row={row}
            showPrintColumns={showPrintColumns}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

export default InventoryOpsVariantTrackingSummary;
